import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { acksApi, peopleApi } from "../api/client.js";
import { CheckInHistoryModal } from "../components/CheckInHistoryModal.jsx";
import { clockTime, lastWeekdays, shortDate, weekdaysBetween, ymd } from "../lib/domain.js";

/* Read from the shared record, not this browser: how each person has done
 * since the day they joined, and every KPI past its review date.
 *
 * Each row covers its own window. A fixed fourteen days flattered whoever
 * arrived last week — they had nothing to miss — and hid the record of
 * everyone who has been here a year. Starting each person on the day they
 * joined is the honest comparison, at the cost of rows no longer being the
 * same length, which the strip below handles rather than pretends away.
 *
 * Today is not a miss until the day is over, so today's empty cell is drawn
 * differently from a miss and left out of the rate. */

/* How many squares a row draws. A record longer than this shows its recent
   end and a chip for the rest: a year is some 260 weekdays, and 260 squares
   is a scrollbar, not a glance. A shorter record simply draws fewer.
 *
   Every row stops at the same number so they line up as columns and the
   heading can state it plainly. The figures beside the squares always cover
   the whole window — the squares are the part that has to fit. */
const STRIP_RECENT = 14;

/* How far back the page is willing to ask for at once. The server refuses a
   longer range; this keeps a nonsense join date from being the thing that
   discovers that. */
const MAX_YEARS = 4;

export function AccountabilityPage() {
  const [state, setState] = useState({ loading: true });
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { users } = await peopleApi.list();
        const people = users.filter((u) => u.daily && u.active);

        /* One request covers everybody: the range runs from the earliest
           join date to today, and each row takes the slice it needs. */
        const today = ymd(new Date());
        const floor = new Date();
        floor.setFullYear(floor.getFullYear() - MAX_YEARS);
        const starts = people
          .map((p) => startFor(p))
          .filter(Boolean)
          .sort();
        const from = starts.length ? maxDate(starts[0], ymd(floor)) : lastWeekdays(14)[0];

        const [{ history }, { missed }] = await Promise.all([
          acksApi.historyRange(from, today),
          acksApi.missedReviews(),
        ]);
        if (!live) return;
        setState({ loading: false, people, history, missed });
      } catch (e) {
        if (live) setState({ loading: false, error: e.message });
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const today = ymd(new Date());

  /* Each person's window, worked out once rather than on every render. */
  const windows = useMemo(() => {
    const out = new Map();
    for (const p of state.people || []) {
      const start = startFor(p);
      /* No join date on the row — an old record, or one the server did not
         send — falls back to the window this page used to show. */
      out.set(p.email, start ? weekdaysBetween(start, today) : lastWeekdays(14));
    }
    return out;
  }, [state.people, today]);

  return (
    <div className="page">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">Accountability</h2>
          <p className="pagesub">
            Read from the shared record, not this browser.
          </p>
        </div>
        <Link className="btn btn-sm" to="/">
          Back to the board
        </Link>
      </div>

      {state.error && (
        <div className="notice">
          <b>Could not read the record.</b> {state.error}
        </div>
      )}

      <div className="sechead">
        <span className="label">
          Daily check-back &middot; since each person joined &middot; last{" "}
          {STRIP_RECENT} weekdays shown
        </span>
        <span className="rule" />
      </div>

      {state.loading ? (
        <p className="empty">Reading the record…</p>
      ) : !state.people?.length ? (
        <p className="empty">Nobody is marked as a daily reviewer yet.</p>
      ) : (
        <div className="adh">
          {state.people.map((p) => {
            const days = windows.get(p.email) || [];
            const stats = tally(days, state.history, p.email, today);
            /* A long record shows its recent end; a short one shows all of
               it. The chip says how much is out of sight, and opens it. */
            const visible = days.slice(-STRIP_RECENT);
            const hidden = days.length - visible.length;

            return (
              <div className="adhrow" key={p.email}>
                <button
                  className="who linkish"
                  type="button"
                  onClick={() => setOpen({ person: p, days, tab: "all" })}
                  title="Open the full record"
                >
                  {p.name || p.email}
                </button>

                <div className="cells">
                  {visible.map((d) => {
                    const got = state.history?.[d]?.[p.email];
                    if (d === today && !got) {
                      return <i key={d} className="cell now" title={d + " — today"} />;
                    }
                    if (!got) {
                      return <i key={d} className="cell no" title={d + " — not acknowledged"} />;
                    }
                    return (
                      <i key={d} className="cell yes" title={d + " — " + clockTime(got.at)} />
                    );
                  })}
                  {hidden > 0 && (
                    <button
                      className="moredays"
                      type="button"
                      onClick={() => setOpen({ person: p, days, tab: "all" })}
                      title={"Open all " + days.length + " weekdays since they joined"}
                    >
                      +{hidden} earlier
                    </button>
                  )}
                </div>

                <span className="since">{sinceLabelFor(p, days)}</span>

                <button
                  className={"score linkish" + (stats.missed ? " bad" : " ok")}
                  type="button"
                  onClick={() => setOpen({ person: p, days, tab: "missed" })}
                  title="Which days were missed"
                >
                  {stats.counted === 0
                    ? "no full day yet"
                    : stats.missed
                      ? stats.missed + " missed"
                      : "no misses"}
                </button>

                <button
                  className="rate linkish"
                  type="button"
                  onClick={() => setOpen({ person: p, days, tab: "kept" })}
                  title="Which days were acknowledged"
                >
                  {stats.rate === null ? "—" : stats.rate + "%"}
                </button>
              </div>
            );
          })}
          <div className="adhkey">
            <span>
              <i className="cell yes" /> acknowledged
            </span>
            <span>
              <i className="cell no" /> missed
            </span>
            <span>
              <i className="cell now" /> today, still open
            </span>
            <span className="adhhint">
              The squares are the last {STRIP_RECENT} weekdays; the figures cover
              the whole record. Press one to see the days.
            </span>
          </div>
        </div>
      )}

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">KPI reviews missed</span>
        <span className="rule" />
        <span className="acts">
          {state.missed?.length > 0 && (
            <span className="pend bad">{state.missed.length}</span>
          )}
        </span>
      </div>

      {state.loading ? null : !state.missed?.length ? (
        <p className="empty">No KPI is past its review date.</p>
      ) : (
        <div className="rows">
          {state.missed.map((m, i) => (
            <div className="missrow" key={i}>
              <span className="days">{m.over}d</span>
              <span className="what">
                <b>{m.name}</b>
                <i>
                  <Link to={"/playground/" + m.playgroundId}>{m.playground}</Link>
                </i>
              </span>
              <span className="fig">
                {m.value || "—"}
                {m.target ? " / " + m.target : ""}
                {m.unit ? " " + m.unit : ""}
              </span>
              <span className="when">
                due {m.reviewOn}
                {m.lastReviewedAt
                  ? " · last reviewed " + ymd(new Date(m.lastReviewedAt))
                  : " · never reviewed"}
              </span>
            </div>
          ))}
        </div>
      )}

      {open && (
        <CheckInHistoryModal
          person={open.person}
          days={open.days}
          history={state.history}
          initialTab={open.tab}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/* The day a person's record starts: the day they joined. */
export function startFor(person) {
  if (!person?.joinedAt) return null;
  const d = new Date(person.joinedAt);
  return isNaN(d.getTime()) ? null : ymd(d);
}

/* Acknowledged, missed and the rate over a window. Today is counted only
   once it is over, so an open day neither helps nor hurts. */
export function tally(days, history, email, today) {
  let missed = 0;
  let counted = 0;
  for (const d of days) {
    const got = history?.[d]?.[email];
    if (d === today && !got) continue;
    counted++;
    if (!got) missed++;
  }
  return {
    missed,
    counted,
    rate: counted ? Math.round(((counted - missed) / counted) * 100) : null,
  };
}

function sinceLabelFor(person, days) {
  const start = startFor(person);
  if (!start) return "last " + days.length + " weekdays";
  return "since " + shortDate(start);
}

const maxDate = (a, b) => (a > b ? a : b);
